/**
 * Disposition in der App: Fahrzeug-Stammdaten, Einsatzregeln aus dem
 * Gedächtnis laden. Geprüft wird im Rechenkern (pruefeEinsatz).
 */

import { istAnhaenger, fahrzeugName, einsatzFahrzeugIds, einsatzMitarbeiterIds } from '@optero/kalkulation-core';
import { supabase, AI_FUNCTION } from './supabaseClient';

export const FAHRZEUG_TYP = { transporter: 'Transporter', pkw: 'PKW', lkw: 'LKW' };
export const ANTRIEB = { diesel: 'Diesel', benzin: 'Benzin', elektro: 'Elektro', hybrid: 'Hybrid', gas: 'Gas' };
export const AUSSTATTUNG = [
    'Dachträger', 'Leiterträger', 'Plattenträger', 'Regalsystem', 'Werkbank', 'Standheizung', 'Ladebordwand', 'Rückfahrkamera',
];

const tonnen = (kg) => `${(kg / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} t`;

/** Kurzbeschreibung für Listen und die KI: „Transporter · 3 Sitze · AHK 2 t · Elektro 300 km“ */
export function fahrzeugBeschreibung(f) {
    if (istAnhaenger(f)) {
        return [
            'Anhänger',
            f.zul_gesamtgewicht_kg && tonnen(f.zul_gesamtgewicht_kg),
            f.gebremst === false ? 'ungebremst' : f.gebremst ? 'gebremst' : null,
            f.ladelaenge_cm && `${(f.ladelaenge_cm / 100).toLocaleString('de-DE')} m Ladefläche`,
        ].filter(Boolean).join(' · ');
    }
    return [
        FAHRZEUG_TYP[f.typ],
        f.sitzplaetze && `${f.sitzplaetze} Sitze`,
        f.zul_gesamtgewicht_kg && tonnen(f.zul_gesamtgewicht_kg),
        f.anhaengerkupplung && (f.anhaengelast_kg ? `AHK ${tonnen(f.anhaengelast_kg)}` : 'AHK'),
        f.antrieb === 'elektro' ? `Elektro${f.reichweite_km ? ` ${f.reichweite_km} km` : ''}` : ANTRIEB[f.antrieb],
        f.ladelaenge_cm && `${(f.ladelaenge_cm / 100).toLocaleString('de-DE')} m Ladelänge`,
        ...(f.ausstattung || []),
    ].filter(Boolean).join(' · ');
}

/** „Ford Transit Custom (FL TR 2026)“ — zwei gleiche Crafter brauchen das Kennzeichen. */
export function fahrzeugTitel(f) {
    const k = String(f?.kennzeichen || '').trim();
    return k ? `${fahrzeugName(f)} (${k})` : fahrzeugName(f);
}

/**
 * Einsatzregeln aus dem Gedächtnis. Die Function übersetzt neue oder geänderte
 * Regeln; fällt sie aus, gilt der zuletzt übersetzte Stand.
 */
export async function ladeEinsatzRegeln() {
    try {
        const { data, error } = await supabase.functions.invoke(AI_FUNCTION, { body: { action: 'einsatz-regeln' } });
        if (!error && Array.isArray(data?.regeln)) return data.regeln;
        console.warn('[einsatz-regeln]', error?.message || data?.error);
    } catch (e) {
        console.warn('[einsatz-regeln]', e.message);
    }
    const { data } = await supabase.from('optimate_memory')
        .select('id, satz, disposition').eq('status', 'aktiv').not('disposition', 'is', null);
    return (data || []).filter((r) => r.disposition?.plantafel).map((r) => ({ id: r.id, satz: r.satz, ...r.disposition }));
}

/** Fahrzeuge + Regeln für die KI-Disposition (Wortlaut der Regeln, Belegung je Fahrzeug). */
export function dispositionFuerKI(fahrzeuge = [], einsaetze = [], regeln = []) {
    const heute = new Date().toISOString().slice(0, 10);
    return {
        fahrzeuge: fahrzeuge.map((f) => ({
            id: String(f.id),
            name: fahrzeugTitel(f),
            beschreibung: fahrzeugBeschreibung(f),
            busy_periods: einsaetze
                .filter((e) => !e.is_deleted && (e.end_date || e.start_date) >= heute && einsatzFahrzeugIds(e).includes(String(f.id)))
                .map((e) => ({ from: e.start_date, to: e.end_date || e.start_date })),
        })),
        regeln: regeln.map((r) => r.satz),
    };
}

export { einsatzFahrzeugIds, einsatzMitarbeiterIds };
